import React from 'react';

import { getBlobFileManagerUrl } from '../config/appConfig';
import { useMsal } from '@azure/msal-react';
import { getAccessToken } from './authService';

// Generic fetchPublications with optional tag/filter
export async function fetchPublications({ msalInstance, account, tag, tags, ...filters } = {}) {
    const accessToken = await getAccessToken(msalInstance, account);
    const apiUrl = getBlobFileManagerUrl();
    const payload = { action: 'list', ...filters };
    const effectiveTags = tags || tag;
    if (effectiveTags) payload.outlooks = Array.isArray(effectiveTags) ? effectiveTags : [effectiveTags];
    const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        let errText;
        try { errText = await response.text(); } catch (e) { errText = response.statusText; }
        throw new Error(errText || 'Failed to fetch publications');
    }
    const data = await response.json();
    return Array.isArray(data.blobs) ? data.blobs : [];
}

// Upload publication (file required)
export async function uploadPublication({ msalInstance, account, title, description, outlooks, publishDate, file }) {
    if (!file) throw new Error('Please select a file.');
    const accessToken = await getAccessToken(msalInstance, account);
    const base64File = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
    // Ensure outlooks is always an array
    const normalizedOutlooks = Array.isArray(outlooks)
      ? outlooks
      : typeof outlooks === 'string'
        ? outlooks.split(',').map(s => s.trim()).filter(Boolean)
        : [];
    const payload = {
        action: 'overwrite',
        fileName: file.name,
        file: base64File,
        outlooks: normalizedOutlooks,
        title,
        description,
        publishDate: publishDate || '',
        uploadedBy: account?.username || 'unknown',
    };
    const apiUrl = getBlobFileManagerUrl();
    const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        let errText;
        try { errText = await response.text(); } catch (e) { errText = response.statusText; }
        throw new Error(errText || 'Upload failed');
    }
}

// Update publication (file optional)
export async function updatePublication({ msalInstance, account, pub, title, description, outlooks, publishDate, file }) {
    const accessToken = await getAccessToken(msalInstance, account);
    // Ensure outlooks is always an array
    const normalizedOutlooks = Array.isArray(outlooks)
      ? outlooks
      : typeof outlooks === 'string'
        ? outlooks.split(',').map(s => s.trim()).filter(Boolean)
        : [];
    let payload = {
        fileName: pub.name,
        outlooks: normalizedOutlooks,
        title,
        description,
        publishDate: publishDate || '',
        uploadedBy: account?.username || 'unknown',
    };
    let action = 'updateMetadata';
    if (file) {
        const base64File = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
        payload.file = base64File;
        action = 'overwrite';
    }
    payload.action = action;
    const apiUrl = getBlobFileManagerUrl();
    const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        let errText;
        try { errText = await response.text(); } catch (e) { errText = response.statusText; }
        throw new Error(errText || 'Update failed');
    }
}

// Delete publication
export async function deletePublication({ msalInstance, account, pub }) {
    const accessToken = await getAccessToken(msalInstance, account);
    const apiUrl = getBlobFileManagerUrl();
    const payload = { action: 'delete', fileName: pub.name };
    const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        let errText;
        try { errText = await response.text(); } catch (e) { errText = response.statusText; }
        throw new Error(errText || 'Delete failed');
    }
}

// Optional: React hook for fetching publications
export function usePublications({ tag, tags, ...filters } = {}) {
    const { instance, accounts } = useMsal();
    const account = accounts[0];
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const pubs = await fetchPublications({ msalInstance: instance, account, tags: tags || tag, ...filters });
            setData(pubs);
        } catch (err) {
            setError(err.message || 'Failed to fetch publications');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [instance, account, tag, JSON.stringify(tags), JSON.stringify(filters)]);

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    console.log('usePublications - data:', data, 'loading:', loading, 'error:', error);
    return { data, loading, error, refetch: fetchData };
}